import React, { useState, useEffect, useMemo } from 'react';
import { Dialog, Typography, Box, Menu, CircularProgress } from '@mui/material';
import GameButtons from './GameButtons';
import { useDispatch, useSelector } from 'react-redux';
import { casinoBetPrices } from '../../store/slices/betSlice';
import SetButtonValue from '../CasinoBet/Dragon_Tiger/SetButtonValue';
import styles from './styles.module.css';
import { GetEditStake, UpdateEditStake } from '../../store/slices/sportsSlice';
import { toast } from 'react-toastify';
import { showError, showSuccess } from "../../utils/toast";

const EditStack = ({ open, onClose, amountList, onUpdate }) => {
    const dispatch = useDispatch();
    const { betPrices } = useSelector((state) => state.bet.casinoBetPrices);
    const { userId } = useSelector((state) => state.auth);
    const editStakeData = useSelector((state) => state.sports.GetEditStakeData || []);

    const [selectedOption, setSelectedOption] = useState('Game Buttons');
    const [filter, setFilter] = useState("Game Buttons");
    const [anchorEl, setAnchorEl] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    const filterOptions = ["Game Buttons", "Casino Buttons"];

    // Fetch data when dialog opens
    useEffect(() => {
        if (open && userId) {
            setIsLoading(true);
            Promise.all([
                dispatch(GetEditStake({ userId, GAME: 'GAME' })),
                dispatch(GetEditStake({ userId, GAME: 'CASINO' })),
                dispatch(casinoBetPrices({ userId }))
            ]).finally(() => setIsLoading(false));
        }
    }, [open, dispatch, userId]);

   
// Fresh initialValues with proper filtering
const initialValues = useMemo(() => {
    const allData = editStakeData || [];

    if (selectedOption === 'Game Buttons') {
        return allData
            .filter(item => item.type === 'GAME') 
            .map((item) => ({
                label: item.overridden ? item.userLabel : item.globalLabel,
                value: item.overridden 
                    ? item.userValue.toString() 
                    : item.globalValue.toString(),
                globalLabel: item.globalLabel,
            }));
    } 
    else {  
        return allData
            .filter(item => item.type === 'CASINO')   
            .map((item) => ({
                label: item.overridden ? item.userLabel : item.globalLabel,
                value: item.overridden 
                    ? item.userValue.toString() 
                    : item.globalValue.toString(),
                globalLabel: item.globalLabel,
            originalLabel: item.globalLabel,   
            }));
    }
}, [selectedOption, editStakeData]); 

    const handleFilterClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleFilterClose = () => {
        setAnchorEl(null);
    };

    const handleFilterSelect = (option) => {
        setFilter(option);
        setSelectedOption(option);
        setAnchorEl(null);
    };

const handleUpdateAmountList = async (selectedValues) => {
    try {
        const gameType = selectedOption === 'Game Buttons' ? 'GAME' : 'CASINO';

        const updatePromises = selectedValues.map((item) =>
            dispatch(UpdateEditStake({
                userId,
                GAME: gameType,
                globalLabel: item.globalLabel ?? item.label,
                newLabel: item.label,
                newValue: parseFloat(item.value) || 0
            }))
        );

        await Promise.all(updatePromises);
        
        // ✅ Refetch both so Redux store is fresh for both tabs
        await Promise.all([
            dispatch(GetEditStake({ userId, GAME: 'GAME' })),
            dispatch(GetEditStake({ userId, GAME: 'CASINO' })),
        ]);
        
        showSuccess('Update Button Value');
    } catch (error) {
        showError(`Failed: ${error?.message || 'Something went wrong'}`);
    }
};

    return (
        <Dialog 
            open={open} 
            onClose={onClose}
            className={styles.betDialogOpen_container}
            PaperProps={{
                style: {
                    position: 'absolute',
                    top: 0,
                    backgroundColor: 'none !important',
                    width: '95%',
                    borderRadius: '0px'
                },
            }}
        >
            <Box className={styles.Dialog_header}>
                <Typography className={styles.betDialog_header_txt}>Set Button Value</Typography>
                <span onClick={onClose}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                        <g clipPath="url(#clip0_6976_19069)">
                            <path d="M0.586047 12.0001C0.470144 12.0001 0.356837 11.9657 0.26046 11.9014C0.164083 11.837 0.0889654 11.7455 0.0446082 11.6384C0.000250996 11.5313 -0.0113527 11.4135 0.0112649 11.2998C0.0338824 11.1861 0.0897052 11.0817 0.171672 10.9998L10.9998 0.17164C11.1097 0.0617407 11.2588 0 11.4142 0C11.5696 0 11.7186 0.0617407 11.8285 0.17164C11.9384 0.281539 12.0002 0.430594 12.0002 0.586015C12.0002 0.741436 11.9384 0.890491 11.8285 1.00039L1.00042 11.8285C0.946056 11.883 0.881462 11.9262 0.810351 11.9556C0.739239 11.9851 0.663012 12.0002 0.586047 12.0001Z" fill="white" />
                            <path d="M11.4141 12.0001C11.3372 12.0002 11.2609 11.9851 11.1898 11.9556C11.1187 11.9262 11.0541 11.883 10.9998 11.8285L0.17164 1.00039C0.0617407 0.890491 0 0.741436 0 0.586015C0 0.430594 0.0617407 0.281539 0.17164 0.17164C0.281539 0.0617407 0.430594 0 0.586015 0C0.741436 0 0.890491 0.0617407 1.00039 0.17164L11.8285 10.9998C11.9105 11.0817 11.9663 11.1861 11.9889 11.2998C12.0115 11.4135 11.9999 11.5313 11.9556 11.6384C11.9112 11.7455 11.8361 11.837 11.7397 11.9014C11.6433 11.9657 11.53 12.0001 11.4141 12.0001Z" fill="white" />
                        </g>
                        <defs>
                            <clipPath id="clip0_6976_19069">
                                <rect width="12" height="12" fill="white" />
                            </clipPath>
                        </defs>
                    </svg>
                </span>
            </Box>

            <Box sx={{ width: '100%' }}>
                <Box className={styles.otherContent_container}>
                    <Box onClick={handleFilterClick} className={styles.Menu_container}>
                        <Typography className={styles.Menu_txt}>{filter}</Typography>
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path d="M4 6L8 10L12 6" stroke="#1E1E1E" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </Box>

                    <Menu
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl)}
                        onClose={handleFilterClose}
                        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                        transformOrigin={{ vertical: "top", horizontal: "center" }}
                        PaperProps={{ className: styles.customMenu }}>
                        {filterOptions.map((option) => (
                            <Box
                                key={option}
                                onClick={() => handleFilterSelect(option)}
                                sx={{ cursor: "pointer" }}
                                className={`${styles.customMenuItem} ${filter === option ? styles.customMenuItemSelected : ""}`}>
                                <Typography
                                    className={`${styles.customMenuItemText} ${filter === option ? styles.customMenuItemSelectedText : ""}`}>
                                    {option}
                                </Typography>
                            </Box>
                        ))}
                    </Menu>
                </Box>

                {/* Content Area */}
               {isLoading ? (
    <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
        <CircularProgress />
    </Box>
) : (
    <Box key={selectedOption} sx={{ width: '100%', minHeight: '450px' }}>
        {selectedOption === 'Game Buttons' ? (
            <GameButtons
                onUpdate={handleUpdateAmountList}
                initialValues={initialValues}
                hideHeader={true}
                onClose={onClose}
            />
        ) : (
            <SetButtonValue
                onUpdate={handleUpdateAmountList}
                initialValues={initialValues}
                hideHeader={true}
                onClose={onClose}
            />
        )}
    </Box>
)}
            </Box>
        </Dialog>
    );
};

export default EditStack;